import React from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";

import { faCircleCheck } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import type { CompetencySuggestion, EditableCompetencyFields } from "../types";

import { resolveCompetencies, useCompetencyCreation } from "../hooks/useCompetencyCreation";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type CompetencyEditPanelProps = {
	onClose: () => void;
};

const CompetencyEditCard: React.FC<{
	suggestion: CompetencySuggestion;
	editedFields: Partial<EditableCompetencyFields> | undefined;
	onChange: (id: string, fields: Partial<EditableCompetencyFields>) => void;
	disabled: boolean;
}> = ({ suggestion, editedFields, onChange, disabled }) => {
	const name = editedFields?.name ?? suggestion.name;
	const description = editedFields?.description ?? suggestion.description;
	const levels = editedFields?.levels ?? suggestion.levels;

	return (
		<div className="flex min-w-0 flex-col gap-4 rounded-lg border border-solid border-neutral-3 bg-white p-4">
			{/* Name */}
			<input
				className="border-0 border-b border-solid border-neutral-3 bg-transparent pb-1 text-base font-semibold text-neutral-9 outline-none transition-colors focus:border-primary-4 disabled:cursor-default disabled:opacity-60"
				value={name}
				disabled={disabled}
				onChange={(e) => onChange(suggestion.id, { name: e.target.value })}
				aria-label="Competency name"
				placeholder="Competency name"
			/>

			{/* Description */}
			<div className="flex flex-col gap-1">
				<Text size="caption" weight="semi-bold" color="neutral-6">
					Description
				</Text>
				<textarea
					className="resize-none rounded bg-neutral-1 p-2 text-sm text-neutral-8 outline-none transition-colors focus:ring-1 focus:ring-primary-4 disabled:cursor-default disabled:opacity-60"
					value={description}
					rows={3}
					disabled={disabled}
					onChange={(e) => onChange(suggestion.id, { description: e.target.value })}
					aria-label="Competency description"
				/>
			</div>

			{/* Levels */}
			<div className="flex flex-col gap-3">
				<Text size="caption" weight="semi-bold" color="neutral-6">
					Levels
				</Text>
				{levels.map((level, idx) => (
					<div key={level.rank} className="flex flex-col gap-1">
						<div className="flex items-center gap-2">
							<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-1 text-xs font-semibold text-primary-6">
								{level.rank}
							</span>
							<Text size="caption" weight="semi-bold" color="neutral-8">
								{level.name}
							</Text>
						</div>
						<textarea
							className="resize-none rounded bg-neutral-1 p-2 text-sm text-neutral-7 outline-none transition-colors focus:ring-1 focus:ring-primary-4 disabled:cursor-default disabled:opacity-60"
							value={level.description}
							rows={3}
							disabled={disabled}
							onChange={(e) => {
								const updated = levels.map((l, i) =>
									i === idx ? { ...l, description: e.target.value } : l
								);
								onChange(suggestion.id, { levels: updated });
							}}
							aria-label={`${level.name} level description`}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export const CompetencyEditPanel: React.FC<CompetencyEditPanelProps> = ({ onClose }) => {
	const framework = useAskPerformYardStore((s) => s.competencyFramework);
	const updateEditedCompetencyFields = useAskPerformYardStore((s) => s.updateEditedCompetencyFields);
	const { saveFramework, isSaving } = useCompetencyCreation();

	if (!framework) return null;

	const { suggestions, editedFields, status } = framework;
	const isSaved = status === "saved";

	const resolvedCompetencies = resolveCompetencies(suggestions, editedFields);

	return (
		<div className="flex h-full min-w-0 flex-col overflow-hidden">
			{/* Header Row */}
			<div className="box-border flex w-full shrink-0 items-center gap-2 bg-white p-2 pr-6">
				<div className="flex min-w-0 flex-1 items-center">
					<div className="flex items-center justify-center p-2">
						<ButtonIcon
							icon={faXmark}
							onClick={onClose}
							variant="standard"
							color="neutral"
							shade={5}
							size={20}
							aria-label="Close edit panel"
						/>
					</div>
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
						Edit Competencies
					</Text>
				</div>
				{isSaved ? (
					<ButtonStandard
						variant="text"
						color="green"
						size="medium"
						iconLeft={faCircleCheck}
						dangerouslySetClassName="!w-auto !cursor-default"
					>
						Saved
					</ButtonStandard>
				) : (
					<ButtonStandard
						variant="text"
						color="primary"
						size="medium"
						onClick={() => saveFramework(resolvedCompetencies)}
						disabled={isSaving}
						dangerouslySetClassName="!w-auto"
					>
						{isSaving ? "Saving..." : "Approve & Save"}
					</ButtonStandard>
				)}
			</div>

			{/* Content — scrollable */}
			<div className="min-w-0 flex-1 overflow-y-auto bg-neutral-1 px-4 pb-6 pt-4">
				<div className="mx-auto flex w-[480px] min-w-0 flex-col gap-4">
					{suggestions.map((s) => (
						<CompetencyEditCard
							key={s.id}
							suggestion={s}
							editedFields={editedFields[s.id]}
							onChange={updateEditedCompetencyFields}
							disabled={isSaving || isSaved}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
